import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

/**
 * The sticky top bar: brand on the left, menu on the right. The offline banner rides underneath
 * the brand row rather than above it, so the one fixed landmark on the screen — the way home —
 * never shifts down when the connection drops (§7).
 */
export default function SiteHeader({ isAdmin, online }: { isAdmin: boolean; online: boolean }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const toggle = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Tapping an item navigates; so does the back button. Both should leave the menu behind.
  useEffect(() => {
    close();
  }, [pathname]);

  /*
   * The page behind the scrim holds still while the menu is up: a phone reads a stray drag on
   * the dim as "scroll the schedule", and the menu is anchored to the header, so the two would
   * come apart. The class goes on <html>, and the scrim's own `touch-action` finishes the job.
   */
  useEffect(() => {
    document.documentElement.classList.toggle('menu-locked', open);
    return () => document.documentElement.classList.remove('menu-locked');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  /**
   * Focus moves only when it has to. The panel is about to go `inert`, so anything focused
   * inside it is about to be dropped — leaving the next Tab to restart from the top of the
   * document — and gets pulled back to the button first. When focus is already outside, which
   * is every open that began with a tap or a click, it stays exactly where it is: re-focusing
   * the button on the way out is what put a focus ring on it for people who had touched the
   * keyboard only to press Escape.
   */
  function close() {
    if (panel.current?.contains(document.activeElement)) toggle.current?.focus();
    setOpen(false);
  }

  return (
    <>
      <div className="topbar">
        <div className="site-header">
          <Link className="brand" to="/">
            <img src="/mark.svg" alt="" width={28} height={28} />
            <span>Topspin App</span>
          </Link>
          <button
            ref={toggle}
            type="button"
            className="icon-button"
            aria-label="Menu"
            aria-expanded={open}
            aria-controls="site-menu"
            onClick={() => setOpen(true)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M4 7h16M4 12h16M4 17h16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {!online && <div className="banner">Offline — showing the last schedule you loaded.</div>}
      </div>

      <div className={`menu-backdrop${open ? ' open' : ''}`} onClick={close} />
      <div id="site-menu" ref={panel} className={`menu${open ? ' open' : ''}`} inert={!open}>
        <nav aria-label="Menu">
          <NavLink to="/" end>
            Home
          </NavLink>
          <NavLink to="/season">Season</NavLink>
          {isAdmin && (
            <>
              <NavLink to="/reminders">Reminders</NavLink>
              <NavLink to="/usage">Usage</NavLink>
            </>
          )}
          <NavLink to="/settings">Settings</NavLink>
        </nav>
      </div>
    </>
  );
}
